import {
  ImsPermissions,
  OrderManagementPermissions,
  ReturnsAndClaimsPermissions,
} from "@redotech/account-common/permissions";
import { isThirdPartyLogistics } from "@redotech/account-common/team-type";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import { defaultChildRoute } from "../app/navigation/routing/legacy-redirects";
import { getPersonalizedInsertRouteChildren } from "../app/navigation/routing/personalized-insert-routes";
import { MerchantRouteNavigate } from "../redo-suite/merchant-routes";
import {
  TranslatedBreadcrumbLabel,
  TranslatedBreadcrumbLayout,
} from "../return/returns-breadcrumb-label";
import {
  WarehouseMetricsLayout,
  WarehouseMetricsLegacyRedirect,
  warehouseMetricsBreadcrumb,
} from "./warehouse-metrics-layout";

const orderBreadcrumb = breadcrumbSlot();
const folderBreadcrumb = breadcrumbSlot();
const viewBreadcrumb = breadcrumbSlot();
const ordersListBreadcrumb = breadcrumbSlot();
const giftReceiptsBreadcrumb = breadcrumbSlot();
const orderEditsBreadcrumb = breadcrumbSlot();
// Slots show the fulfillment source breadcrumb when `sourceView` is present.
const orderSourceFulfillmentsBreadcrumb = breadcrumbSlot();
const orderSourceViewBreadcrumb = breadcrumbSlot();

export const scanRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Scan" },
    children: [
      {
        index: true,
        async lazy() {
          const { ScanStartPage } =
            await import("@redotech/redo-merchant-fulfillment-app/scan/start-page");
          return { Component: ScanStartPage };
        },
      },
      {
        path: ":fulfillmentGroupId",
        async lazy() {
          const { ScanFulfillmentPage: ScanFulfillmentPage } =
            await import("@redotech/redo-merchant-fulfillment-app/scan/fulfillment-page");
          return { Component: ScanFulfillmentPage };
        },
      },
    ],
  },
];

export const outboundLabelRoutes: PermissionedRouteObject[] = [
  {
    async lazy() {
      const { OutboundLabelsLayout } = await import("./orders-layout-wrapper");
      return { Component: OutboundLabelsLayout };
    },
    children: [
      {
        index: true,
        element: (
          <MerchantRouteNavigate
            routeTo={(routes) => routes.shippingDefaultRoute}
          />
        ),
      },
      {
        path: "dashboard",
        handle: {
          breadcrumb: "Dashboard",
          permission:
            OrderManagementPermissions.VIEW_ORDER_FULFILLMENT_ANALYTICS,
        },
        async lazy() {
          const { DashboardPage } =
            await import("@redotech/redo-merchant-fulfillment-app/dashboard/dashboard");
          return { element: <DashboardPage /> };
        },
      },
      {
        path: "rewards",
        children: [
          {
            handle: { breadcrumb: "Fulfillments", breadcrumbNavigate: false },
            children: [
              {
                handle: {
                  breadcrumb: "Rewards",
                  permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
                },
                index: true,
                async lazy() {
                  const { RewardsPage } =
                    await import("@redotech/redo-merchant-fulfillment-app/rewards/rewards");
                  return { element: <RewardsPage /> };
                },
              },
            ],
          },
        ],
      },
      {
        path: "about",
        handle: { breadcrumb: "Welcome" },
        async lazy() {
          const { AboutOms } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/about-oms/about-oms");
          return { element: <AboutOms /> };
        },
      },
      {
        path: "lookup",
        async lazy() {
          const { OutboundOrderLookup } =
            await import("@redotech/redo-merchant-fulfillment-app/outbound-order-lookup");
          return { element: <OutboundOrderLookup /> };
        },
      },
      {
        path: "order-edits",
        handle: {
          breadcrumb: orderEditsBreadcrumb,
          permission: OrderManagementPermissions.VIEW_ORDER,
        },
        async lazy() {
          const { OrderEditsPage } =
            // redont-allow-dynamic-import: route-level code splitting for carved fulfillment package
            await import("@redotech/redo-merchant-fulfillment-app/order-edits/order-edits-page");
          const { OrderEditInsightBanner } =
            // redont-allow-dynamic-import: route-level code splitting for carved support package
            await import("@redotech/redo-merchant-support-app/analytics/dashboards/support/order-edit-insight-banner");
          return {
            element: (
              <>
                <TranslatedBreadcrumbLabel
                  breadcrumb={orderEditsBreadcrumb}
                  getLabel={(t) => t("Order editing")}
                />
                <OrderEditsPage banner={<OrderEditInsightBanner />} />
              </>
            ),
          };
        },
      },
      {
        path: "bad-warehouse-bins",
        handle: {
          breadcrumb: "Bad warehouse bins",
          permission:
            OrderManagementPermissions.VIEW_ORDER_FULFILLMENT_ANALYTICS,
        },
        async lazy() {
          const { BadWarehouseBinsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/bad-warehouse-bins");
          return { element: <BadWarehouseBinsPage /> };
        },
      },
      {
        path: "admin-pick-sessions",
        children: [
          {
            handle: {
              breadcrumb: "Pick sessions",
              permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
            },
            index: true,
            async lazy() {
              const { PickSessionsTablePage } =
                await import("@redotech/redo-merchant-fulfillment-app/wms/pick-sessions/pick-sessions-table-page");
              return { element: <PickSessionsTablePage /> };
            },
          },
          {
            path: ":pickSessionId",
            handle: { breadcrumb: "Active pick session" },
            async lazy() {
              const { ActivePickSessionPage } =
                await import("@redotech/redo-merchant-fulfillment-app/wms/pick-sessions/active-pick-session-page");
              return { element: <ActivePickSessionPage /> };
            },
          },
          {
            path: ":pickSessionId/tote-scanning",
            handle: { breadcrumb: "Tote scanning" },
            async lazy() {
              const { ToteScanningPage } =
                await import("@redotech/redo-merchant-fulfillment-app/wms/pick-sessions/tote-scanning-page");
              return { element: <ToteScanningPage /> };
            },
          },
          {
            path: ":pickSessionId/route",
            handle: { breadcrumb: "Pick route" },
            async lazy() {
              const { PickSessionRoutePage } =
                await import("@redotech/redo-merchant-fulfillment-app/wms/pick-sessions/pick-session-route-page");
              return { element: <PickSessionRoutePage /> };
            },
          },
          {
            path: "problem-order/:fulfillmentGroupId",
            handle: { breadcrumb: "Problem order" },
            async lazy() {
              const { ProblemOrderDetailsPage } =
                await import("@redotech/redo-merchant-fulfillment-app/wms/pick-sessions/problem-order-details-page");
              return { element: <ProblemOrderDetailsPage /> };
            },
          },
        ],
      },
      {
        path: "pick-sessions",
        children: [
          {
            handle: {
              breadcrumb: "Pick sessions",
              permission: OrderManagementPermissions.MANAGE_PICK_SESSIONS,
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { PickSessionsPage } =
                    await import("@redotech/redo-merchant-fulfillment-app/wms/pick-sessions/pick-sessions-page");
                  return { element: <PickSessionsPage /> };
                },
              },
              {
                path: ":pickSessionId",
                handle: { breadcrumb: "Active pick session" },
                async lazy() {
                  const { ActivePickSessionPage } =
                    await import("@redotech/redo-merchant-fulfillment-app/wms/pick-sessions/active-pick-session-page");
                  return { element: <ActivePickSessionPage /> };
                },
              },
              {
                path: ":pickSessionId/tote-scanning",
                handle: { breadcrumb: "Tote scanning" },
                async lazy() {
                  const { ToteScanningPage } =
                    await import("@redotech/redo-merchant-fulfillment-app/wms/pick-sessions/tote-scanning-page");
                  return { element: <ToteScanningPage /> };
                },
              },
              {
                path: ":pickSessionId/route",
                handle: { breadcrumb: "Pick route" },
                async lazy() {
                  const { PickSessionRoutePage } =
                    await import("@redotech/redo-merchant-fulfillment-app/wms/pick-sessions/pick-session-route-page");
                  return { element: <PickSessionRoutePage /> };
                },
              },
              {
                path: "problem-order/:fulfillmentGroupId",
                handle: { breadcrumb: "Problem order" },
                async lazy() {
                  const { ProblemOrderDetailsPage } =
                    await import("@redotech/redo-merchant-fulfillment-app/wms/pick-sessions/problem-order-details-page");
                  return { element: <ProblemOrderDetailsPage /> };
                },
              },
            ],
          },
        ],
      },
      {
        path: "create-pick-session",
        children: [
          {
            handle: {
              breadcrumb: "Create pick session",
              permission: OrderManagementPermissions.MANAGE_PICK_SESSIONS,
            },
            index: true,
            async lazy() {
              const { CreatePickSessionPage } =
                await import("@redotech/redo-merchant-fulfillment-app/wms/pick-sessions/create-pick-session-page");
              return { element: <CreatePickSessionPage /> };
            },
          },
        ],
      },
      {
        path: "pickups",
        children: [
          {
            handle: {
              breadcrumb: "Carrier pickups (UPS)",
              permission: OrderManagementPermissions.SCAN_AND_VERIFY,
            },
            index: true,
            async lazy() {
              const { PickupsPage } =
                await import("@redotech/redo-merchant-fulfillment-app/common/pickups/pickups-page");
              return { element: <PickupsPage /> };
            },
          },
        ],
      },
      {
        path: "batch-history",
        children: [
          {
            handle: {
              breadcrumb: "Batch history",
              permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
            },
            index: true,
            async lazy() {
              const { BatchHistoryPage } =
                await import("@redotech/redo-merchant-fulfillment-app/batch-history/batch-history");
              return { element: <BatchHistoryPage /> };
            },
          },
        ],
      },
      {
        path: "downloads",
        handle: {
          breadcrumb: "Downloads",
          handle: { permission: OrderManagementPermissions.MANAGE_SHIPMENTS },
        },
        async lazy() {
          const { ViewExportsTablePage } =
            await import("@redotech/redo-merchant-fulfillment-app/view-exports/view-exports-table-page");
          return { element: <ViewExportsTablePage /> };
        },
      },
      {
        path: "bulk-fulfillments",
        children: [
          {
            handle: { breadcrumb: "Fulfillments", breadcrumbNavigate: false },
            children: [
              {
                handle: {
                  breadcrumb: "Bulk fulfillments",
                  permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
                },
                children: [
                  {
                    index: true,
                    async lazy() {
                      const { BulkFulfillmentsPage } =
                        await import("@redotech/redo-merchant-fulfillment-app/bulk-fulfillments/bulk-fulfillments");
                      return { element: <BulkFulfillmentsPage /> };
                    },
                  },
                  {
                    path: ":batchKey",
                    handle: { breadcrumb: "Bulk fulfillment details" },
                    async lazy() {
                      const { BulkFulfillmentDetailsPage } =
                        await import("@redotech/redo-merchant-fulfillment-app/bulk-fulfillments/bulk-fulfillment-details");
                      return { element: <BulkFulfillmentDetailsPage /> };
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "eod-manifest",
        children: [
          {
            handle: {
              breadcrumb: "EOD manifest",
              permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
            },
            index: true,
            async lazy() {
              const { EodManifestPage } =
                await import("@redotech/redo-merchant-fulfillment-app/end-of-day-manifest/eod-manifest-page");
              return { element: <EodManifestPage /> };
            },
          },
        ],
      },
      {
        path: "overbox-labels",
        children: [
          {
            handle: {
              breadcrumb: "Overbox labels",
              permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
            },
            index: true,
            async lazy() {
              const { OverboxLabelsPage } =
                await import("@redotech/redo-merchant-fulfillment-app/overbox-labels/overbox-labels-page");
              return { element: <OverboxLabelsPage /> };
            },
          },
        ],
      },
      {
        path: "personalized-slips",
        handle: {
          breadcrumb: "Personalized inserts",
          permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
        },
        children: getPersonalizedInsertRouteChildren(),
      },
      {
        path: "orders",
        handle: { permission: OrderManagementPermissions.VIEW_ORDER },
        children: [
          {
            element: (
              <TranslatedBreadcrumbLayout
                breadcrumb={ordersListBreadcrumb}
                getLabel={(t) => t("All orders")}
              />
            ),
            handle: { breadcrumb: ordersListBreadcrumb },
            children: [
              {
                index: true,
                async lazy() {
                  const { Orders } =
                    await import("./orders-table/orders-table");
                  return {
                    element: (
                      <Orders
                        filterParams={(filters) => {
                          const picked: Record<string, string> = {};
                          for (const key of [
                            "dateRange",
                            "shipmentStatus",
                            "carrier",
                            "destinationState",
                            "destinationCountry",
                            "originState",
                            "originCountry",
                            "dateFilter",
                          ]) {
                            const value = filters[key];
                            if (value !== undefined) {
                              picked[key] = value;
                            }
                          }
                          return picked;
                        }}
                      />
                    ),
                  };
                },
              },
              {
                // These wrapper layers populate source breadcrumbs under both
                // legacy and current route prefixes.
                handle: {
                  breadcrumb: orderSourceFulfillmentsBreadcrumb,
                  breadcrumbNavigate: false,
                },
                children: [
                  {
                    handle: { breadcrumb: orderSourceViewBreadcrumb },
                    children: [
                      {
                        path: ":orderId",
                        handle: { breadcrumb: orderBreadcrumb },
                        children: [
                          {
                            index: true,
                            async lazy() {
                              const { OrderPage } =
                                await import("./order-page");
                              return {
                                element: (
                                  <OrderPage
                                    breadcrumb={orderBreadcrumb}
                                    sourceFulfillmentsBreadcrumb={
                                      orderSourceFulfillmentsBreadcrumb
                                    }
                                    sourceViewBreadcrumb={
                                      orderSourceViewBreadcrumb
                                    }
                                  />
                                ),
                              };
                            },
                          },
                          {
                            path: "automation-history/:fulfillmentGroupId",
                            handle: {
                              permission:
                                OrderManagementPermissions.RECEIVE_ORDER_UPDATES,
                            },
                            async lazy() {
                              const { AutomationHistoryPage } =
                                await import("./automation-history/automation-history-page");
                              return { Component: AutomationHistoryPage };
                            },
                          },
                          {
                            path: "gift-receipts",
                            handle: { breadcrumb: giftReceiptsBreadcrumb },
                            async lazy() {
                              const { GiftReceiptsPage } =
                                await import("./gift-receipts");
                              return {
                                element: (
                                  <>
                                    <TranslatedBreadcrumbLabel
                                      breadcrumb={giftReceiptsBreadcrumb}
                                      getLabel={(t) => t("Gift receipts")}
                                    />
                                    <GiftReceiptsPage
                                      breadcrumb={orderBreadcrumb}
                                    />
                                  </>
                                ),
                              };
                            },
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "scan",
        children: [
          {
            handle: { permission: OrderManagementPermissions.SCAN_AND_VERIFY },
            async lazy() {
              const { ScanRoot } =
                await import("@redotech/redo-merchant-fulfillment-app/scan/root");
              return { Component: ScanRoot };
            },
            children: scanRoutes,
          },
        ],
      },
      {
        path: "print-jobs",
        children: [
          {
            handle: {
              breadcrumb: "Print jobs",
              permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
            },
            index: true,
            async lazy() {
              const { PrintJobsPage } =
                await import("@redotech/redo-merchant-fulfillment-app/printing/print-jobs");
              return { element: <PrintJobsPage /> };
            },
          },
        ],
      },

      {
        path: "rate-calculator",
        handle: {
          breadcrumb: "Rate calculator",
          permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
        },
        async lazy() {
          const { RateCalculatorPage } =
            await import("@redotech/redo-merchant-fulfillment-app/rate-calculator/rate-calculator-page");
          return { element: <RateCalculatorPage /> };
        },
      },
      {
        path: "personalized-inserts",
        handle: {
          breadcrumb: "Personalized inserts",
          permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
        },
        children: getPersonalizedInsertRouteChildren(),
      },
      {
        path: ":viewName",
        children: [
          {
            handle: { breadcrumb: "Fulfillments", breadcrumbNavigate: false },
            children: [
              {
                handle: {
                  breadcrumb: folderBreadcrumb,
                  breadcrumbNavigate: false,
                  permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
                },
                children: [
                  {
                    index: true,
                    handle: { breadcrumb: viewBreadcrumb },
                    async lazy() {
                      const { FulfillmentGroupPage } =
                        await import("@redotech/redo-merchant-fulfillment-app/fulfillment-groups/fulfillment-group-page");
                      return {
                        element: (
                          <FulfillmentGroupPage
                            folderBreadcrumb={folderBreadcrumb}
                            viewBreadcrumb={viewBreadcrumb}
                          />
                        ),
                      };
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

export const wmsRoutes: PermissionedRouteObject[] = [
  {
    async lazy() {
      const { OrdersLayoutWrapper } = await import("./orders-layout-wrapper");
      return { Component: OrdersLayoutWrapper };
    },
    handle: { breadcrumb: "Warehouse management" },
    children: [
      defaultChildRoute("storage-locations"),
      {
        path: "inbound-receiving",
        handle: {
          breadcrumb: "Inbound receiving",
          permission: ({ team, userPermissions }) =>
            isThirdPartyLogistics(team) &&
            !!userPermissions &&
            [
              ReturnsAndClaimsPermissions.VIEW_RETURN,
              ReturnsAndClaimsPermissions.RECEIVE_RETURN,
              ImsPermissions.ACCESS_INVENTORY,
              OrderManagementPermissions.MANAGE_SHIPMENTS,
            ].some((permission) => userPermissions.has(permission)),
        },
        async lazy() {
          const [{ WmsProvider }, { InboundReceivingPage }] = await Promise.all(
            [
              import("@redotech/redo-merchant-fulfillment-app/common/wms-context"),
              import("@redotech/redo-merchant-returns-processing-app/receiving-source/inbound-receiving-page"),
            ],
          );
          return {
            element: (
              <WmsProvider>
                <InboundReceivingPage />
              </WmsProvider>
            ),
          };
        },
      },
      {
        path: "storage-locations",
        handle: {
          breadcrumb: "Bins",
          permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
        },
        children: [
          {
            index: true,
            async lazy() {
              const { StorageLocationsPage } =
                await import("@redotech/redo-merchant-fulfillment-app/wms/storage-locations/storage-locations-page");
              return { element: <StorageLocationsPage /> };
            },
          },
        ],
      },
      {
        path: "inventory-level-ledger",
        handle: {
          breadcrumb: "Inventory ledger",
          permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
        },
        children: [
          {
            index: true,
            async lazy() {
              const { InventoryLevelLedgerPage } =
                await import("@redotech/redo-merchant-fulfillment-app/wms/inventory-level-ledger/inventory-level-ledger-page");
              return { element: <InventoryLevelLedgerPage /> };
            },
          },
        ],
      },
      {
        path: "lots",
        handle: {
          breadcrumb: "Lots",
          permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
        },
        children: [
          {
            index: true,
            async lazy() {
              const { LotsPage } =
                await import("@redotech/redo-merchant-fulfillment-app/wms/lots/lots-page");
              return { element: <LotsPage /> };
            },
          },
          {
            path: ":inventoryItemId",
            handle: {
              breadcrumb: "Lot detail",
              permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
            },
            async lazy() {
              const { LotDetailPage } =
                // redont-allow-dynamic-import: route-level code splitting
                await import("@redotech/redo-merchant-fulfillment-app/wms/lots/lot-detail-page");
              return { element: <LotDetailPage /> };
            },
          },
        ],
      },
      {
        path: "service-requests",
        handle: {
          breadcrumb: "Service requests",
          permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
        },
        children: [
          {
            index: true,
            async lazy() {
              const { ItemWorkRequestsPage } =
                // redont-allow-dynamic-import: route-level code splitting
                await import("@redotech/redo-merchant-fulfillment-app/wms/item-work-requests/item-work-requests-page");
              return { element: <ItemWorkRequestsPage /> };
            },
          },
        ],
      },
      {
        path: "work-orders",
        handle: {
          breadcrumb: "Work orders",
          permission: OrderManagementPermissions.MANAGE_SHIPMENTS,
        },
        children: [
          {
            index: true,
            async lazy() {
              const { WorkOrdersPage } =
                await import("@redotech/redo-merchant-fulfillment-app/work-orders/work-orders-page");
              return { element: <WorkOrdersPage /> };
            },
          },
          {
            path: "detail/:workOrderId",
            handle: { breadcrumb: "Work order detail" },
            async lazy() {
              const { WorkOrderDetailPage } =
                await import("@redotech/redo-merchant-fulfillment-app/work-orders/work-order-detail-page");
              return { element: <WorkOrderDetailPage /> };
            },
          },
          {
            path: "replenishment",
            handle: { breadcrumb: "Replenishment plan" },
            async lazy() {
              const { ReplenishmentPlanPage } =
                await import("@redotech/redo-merchant-fulfillment-app/work-orders/replenishment-plan-page");
              return { element: <ReplenishmentPlanPage /> };
            },
          },
          {
            path: ":viewName",
            async lazy() {
              const { WorkOrdersPage } =
                await import("@redotech/redo-merchant-fulfillment-app/work-orders/work-orders-page");
              return { element: <WorkOrdersPage /> };
            },
          },
        ],
      },
      {
        path: "warehouse-metrics",
        handle: {
          breadcrumb: warehouseMetricsBreadcrumb,
          permission:
            OrderManagementPermissions.VIEW_ORDER_FULFILLMENT_ANALYTICS,
        },
        element: <WarehouseMetricsLayout />,
        children: [
          defaultChildRoute("inbound-dashboard"),
          {
            path: "inbound-dashboard",
            async lazy() {
              const { InboundDashboardPage } =
                // redont-allow-dynamic-import: route-level code splitting for carved analytics package
                await import("@redotech/redo-merchant-analytics-app/analytics/inbound-dashboard/inbound-dashboard-page");
              return { element: <InboundDashboardPage /> };
            },
          },
          {
            path: "processing-pipeline",
            async lazy() {
              const { ProcessingPipelinePage } =
                // redont-allow-dynamic-import: route-level code splitting for carved analytics package
                await import("@redotech/redo-merchant-analytics-app/analytics/processing-pipeline/processing-pipeline-page");
              return { element: <ProcessingPipelinePage /> };
            },
          },
          {
            path: "qa-metrics",
            async lazy() {
              const { QaMetricsPage } =
                // redont-allow-dynamic-import: route-level code splitting for carved analytics package
                await import("@redotech/redo-merchant-analytics-app/analytics/qa-metrics/qa-metrics-page");
              return { element: <QaMetricsPage /> };
            },
          },
          {
            path: "refulfillment",
            async lazy() {
              const { RefulfillmentPage } =
                // redont-allow-dynamic-import: route-level code splitting for carved analytics package
                await import("@redotech/redo-merchant-analytics-app/analytics/refulfillment/refulfillment-page");
              return { element: <RefulfillmentPage /> };
            },
          },
          {
            path: "warehouse-slas",
            async lazy() {
              const { WarehouseSlasPage } =
                // redont-allow-dynamic-import: route-level code splitting for carved analytics package
                await import("@redotech/redo-merchant-analytics-app/analytics/warehouse-slas/warehouse-slas-page");
              return { element: <WarehouseSlasPage /> };
            },
          },
        ],
      },
      {
        path: "inbound-dashboard",
        element: <WarehouseMetricsLegacyRedirect tab="inbound-dashboard" />,
      },
      {
        path: "processing-pipeline",
        element: <WarehouseMetricsLegacyRedirect tab="processing-pipeline" />,
      },
      {
        path: "qa-metrics",
        element: <WarehouseMetricsLegacyRedirect tab="qa-metrics" />,
      },
      {
        path: "refulfillment",
        element: <WarehouseMetricsLegacyRedirect tab="refulfillment" />,
      },
      {
        path: "warehouse-slas",
        element: <WarehouseMetricsLegacyRedirect tab="warehouse-slas" />,
      },
      {
        path: "support/:identifier?",
        handle: {
          breadcrumb: "Support",
          permission: OrderManagementPermissions.VIEW_ORDER,
        },
        async lazy() {
          const { ReturnLifecyclePage } =
            await import("../return-lifecycle/return-lifecycle-page");
          return { element: <ReturnLifecyclePage /> };
        },
      },
    ],
  },
];
